import React, { useEffect } from 'react';
import {
 batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

import Layout from '../../layout/Layout';
import { permissions } from '../../common/permissionGroup';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import ArticlesSubheader from './components/ArticlesSubheader';
import getArticles from '../../thunks/articles/getArticles';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHeadData from '../../components/Table/TableHeadData';
import TableRow from '../../components/Table/TableRow';
import TableHead from '../../components/Table/TableHead';
import articlesTable from '../../constants/articlesTable';
import TableBody from '../../components/Table/TableBody';
import TableData from '../../components/Table/TableData';
import Badge from '../../components/Badge/Badge';
import { articleStatus, articleStatusInfo } from '../../constants/articleStatus';
import ButtonTypes from '../../constants/buttonTypes';
import Button from '../../components/Button/Button';
import {
  IconArchive, IconEye, IconEyeSlash, IconPencil
} from '../../assets/icon';
import patchArticles from '../../thunks/articles/patchArticles';
import { setArticlesFilter } from '../../actions/articles/articlesFilter';

const tableContainerClassName = 'overflow-auto';
const tableCellClassName = 'min-w-max whitespace-nowrap px-[9px] py-0';
const actionButtonClassName = 'm-[2px] p-[5px]';

const ArticlesCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { articles, loading, filter: { order } } = useSelector((state) => ({
    articles: state?.getArticles?.data,
    loading: state?.getArticles?.loading,
    filter: state?.articlesFilter,
  }), shallowEqual);

  useEffect(() => {
    if (!articles?.length) {
      dispatch(getArticles());
    }
  }, []);

  const onChangeStatus = (newStatus, id) => !loading && dispatch(patchArticles(
    {
      id,
      article_status: newStatus,
      ...(newStatus === articleStatus.POSTED
        ? { article_post_date: DateTime.local().setLocale('en').toFormat('yyyy-LL-dd HH:mm:ss') }
        : { article_post_date: '' })
    }
  ));

  const onEdit = (id) => navigate(`/articles/edit/?id=${id}`);

  const onSort = (isSort, sortStr) => {
    if (isSort) {
      batch(() => {
        dispatch(setArticlesFilter({ order: sortStr }));
        dispatch(getArticles());
      });
    }
  };

  return (
    <Layout subheader={<ArticlesSubheader />}>
      <PermissionsGate
        can={permissions.ARTICLES_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          headerBordered={false}
          footerBordered={false}
        >
          <div className={tableContainerClassName}>
            <Table loading={loading}>
              <TableHead>
                <TableRow bordered>
                  <PermissionsGate
                    can={permissions.ARTICLES_UPDATE}
                  >
                    <TableHeadData />
                  </PermissionsGate>
                  {
                    articlesTable.map(({ source, sortable, title }) => (
                      <TableHeadData
                        key={source}
                        sortable={sortable}
                        order={order}
                        source={source}
                        onSort={(val) => onSort(sortable, val)}

                      >
                        {title}
                      </TableHeadData>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  articles?.map(({
                    _id: id,
                    article_status: status,
                    article_title: articleTitle,
                    article_marketplace_name: articleMarketplaceName,
                    article_last_edite: articleLastEdite,
                    article_post_date: articlePostDate
                  }, index) => (
                    <TableRow
                      key={id}
                      bordered={index < articles.length - 1}
                    >
                      <PermissionsGate
                        can={permissions.ARTICLES_UPDATE}
                      >
                        <TableData>
                          <div className="flex flex-row">
                            {status === articleStatus.POSTED ? (
                              <Button
                                tooltip="Hide article"
                                containerStyle={actionButtonClassName}
                                tooltipId="hide"
                                icon={<IconEyeSlash />}
                                type={ButtonTypes.LIGHT_WARNING}
                                disabled={loading}
                                onClick={() => onChangeStatus(articleStatus.HIDDEN, id)}
                              />
                            ) : (
                              <Button
                                tooltip="Post article"
                                containerStyle={actionButtonClassName}
                                tooltipId="post"
                                icon={<IconEye />}
                                type={ButtonTypes.LIGHT_SUCCESS}
                                disabled={loading}
                                onClick={() => onChangeStatus(articleStatus.POSTED, id)}
                              />
                            ) }
                            {status !== articleStatus.ARCHIVE && (
                              <Button
                                tooltip="Move to archive"
                                tooltipId="archive"
                                containerStyle={actionButtonClassName}
                                icon={<IconArchive />}
                                type={ButtonTypes.LIGHT_DANGER}
                                onClick={() => onChangeStatus(articleStatus.ARCHIVE, id)}
                                disabled={loading}
                              />
                            )}
                          </div>
                        </TableData>
                      </PermissionsGate>
                      <TableData className={tableCellClassName}>
                        <Badge text={status} type={articleStatusInfo[status].state} />
                      </TableData>
                      <TableData className={tableCellClassName}>
                        {articleTitle}
                      </TableData>
                      <TableData className={tableCellClassName}>
                        <Badge text={articleMarketplaceName} type={ButtonTypes.LIGHT} />
                      </TableData>
                      <TableData className={tableCellClassName}>
                        { articlePostDate
                          && (
                          <Badge
                            text={DateTime.fromISO(articlePostDate.replace(' ', 'T'))
                              .setLocale('en').toRelative()}
                            type={ButtonTypes.LIGHT}
                          />
                          )}
                      </TableData>
                      <TableData className={tableCellClassName}>
                        <Badge
                          text={DateTime.fromISO(articleLastEdite.replace(' ', 'T'))
                            .setLocale('en').toRelative()}
                          type={ButtonTypes.LIGHT}
                        />
                      </TableData>
                      <TableData>
                        <Button
                          tooltip="Edit article"
                          tooltipId="Edit"
                          containerStyle={actionButtonClassName}
                          icon={<IconPencil />}
                          type={ButtonTypes.LIGHT_DARK}
                          onClick={() => onEdit(id)}
                          disabled={loading}
                        />
                      </TableData>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </div>
        </Card>
      </PermissionsGate>
    </Layout>
  );
};

ArticlesCard.propTypes = {};

ArticlesCard.defaultProps = {};

export default ArticlesCard;
